import { toast } from "@/store";
import { TYPE } from "@/store/modules/toast.module";
import { Http } from "@/utils/http";
import { LocalStorageApp } from "@/utils/localstorage";

/** Authentication, session context, and account-recovery calls. */
export default class LoginService {
  /**
   * Logs in with a username + password (or one-time Slack code, same field).
   * On success, stores the JWT via {@link Http.sync} and returns `true`.
   */
  public static async login(
    username: string,
    passwd: string
  ): Promise<boolean> {
    const targetUrl = `${process.env.VUE_APP_API_AUTH}/?task=login`;
    try {
      const response = await Http.instance
        .post(targetUrl, {
          username: username,
          passwd: passwd,
        })
        .then(async (result: any) => {
          if (result.data.data.token) {
            await LocalStorageApp.setItem("pcp.token", result.data.data.token);
            await Http.sync();
          }
          return true;
        })
        .catch((error: any) => {
          toast.createAndPushToast(
            `${error.response.data.message}`,
            "",
            true,
            TYPE.DANGER
          );
          return false;
        });
      return response;
    } catch (err) {
      toast.createAndPushToast(
        `Une erreur s'est produite`,
        "Veuillez réessayer plus tard",
        true,
        TYPE.DANGER
      );
      return false;
    }
  }

  /** Changes the current user's password. */
  public static async changePassword(
    passwdOld: string,
    passwdNew: string,
    passwdConfirm: string
  ): Promise<boolean> {
    const response = await Http.postTask(
      "updatePassword",
      { passwdOld, passwdNew, passwdConfirm },
      "Mot de passe modifié"
    );
    return !!response.success;
  }

  /** Requests a one-time login code sent to the user via Slack DM. */
  public static async codeSlack(username: string): Promise<boolean> {
    const response = await Http.postTask(
      "slack",
      { username },
      "Code de connexion envoyé sur Slack",
      process.env.VUE_APP_API_AUTH
    );
    return !!response.success;
  }

  /** Fetches the current authenticated user's context/profile. */
  public static async context(): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=context`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /** Fetches the current onboarding step state for the logged-in user. */
  public static async onboarding(): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=onboarding`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  /** Marks onboarding as completed for the current user. */
  public static async onboardingValidation(): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=onboardingValidation`;
    const { data: response } = await Http.instance.post(targetUrl);
    return response.success;
  }

  /**
   * Fetches a user's context for the "unlock session" screen (re-auth after token expiry).
   * The backend derives the user from `token`'s signature (even though it's expired) — it never
   * trusts a client-supplied id, to avoid turning this into an open user-enumeration endpoint.
   */
  public static async contextUnlock(token: string): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_AUTH}/?task=context`;
    const { data: response } = await Http.instance.get(targetUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  /** Fetches the upcoming scheduled maintenance windows for display in the UI. */
  public static async maintenanceSchedule(): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_AUTH}/?task=status`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data.scheduled_maintenances;
  }

  /** Fetches overall backend/service status. */
  public static async status(): Promise<any> {
    const targetUrl = `${process.env.VUE_APP_API_AUTH}/?task=status`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }
}
